import { TimeOffStatus } from '@/modules/time-off/types/time-off-status';

import { ShortProject } from './time-tracking';

export enum ApprovalConfirmMode {
  Approve = 'approve',
  Unapprove = 'unapprove',
}

/**
 * Time entry approval status
 */
export enum TimeEntryStatus {
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

/**
 * Short member info for approvals table
 */
export interface ApprovalMember {
  id: number;
  name: string;
  avatarUrl: string | null;
}

/**
 * Individual time entry in approvals table (leaf level).
 * Enriched with denormalized project/member fields for modal display and API hash-based concurrency.
 */
export interface ApprovalTimeEntry {
  id: number;
  date: string; // ISO format
  trackedHours: number;
  approvedForInvoiceHours: number;
  approvedForBillHours: number;
  approvalNotes: string | null;
  description: string;
  status: TimeEntryStatus;
  // Denormalized project fields
  projectId: number;
  projectName: string;
  projectColorHex: string;
  projectHash: string;
  // Denormalized member fields
  memberId: number;
  memberName: string;
  memberAvatarUrl: string | null;
  memberHash: string;
  /** External rate as of the entry date, in `currencyCode`. */
  externalRate: number | null;
  /** Approved-for-invoice hours × external rate; null unless the entry is approved. */
  approvedIncome: number | null;
  currencyCode: string | null;
  // Concurrency hash required by approve/unapprove API
  hash: string;
}

/**
 * Informational time-off day row interleaved into a member's time entries (leaf level).
 * Not selectable, not approvable — rendered read-only alongside worklog entries.
 */
export interface ApprovalTimeOffEntry {
  /** Synthetic row id (`timeoff-{dayId}`) — disjoint from worklog `entry-{id}` ids. */
  id: string;
  date: string; // ISO format
  timeOffTypeName: string;
  durationHours: number;
  status: TimeOffStatus;
  description: string | null;
}

/** A leaf row of the approvals tree: a real worklog entry or an informational time-off day. */
export type ApprovalEntryRow = ApprovalTimeEntry | ApprovalTimeOffEntry;

/**
 * Member row in approvals table (second level)
 * Contains aggregated hours and list of time entries
 */
export interface ApprovalMemberRow {
  id: string;
  member: ApprovalMember;
  trackedHours: number;
  /**
   * Tracked hours of the entries still awaiting approval. This is a TRACKED figure for display —
   * what an approve would actually write is {@link pendingApprovedForInvoiceHours}.
   */
  pendingTrackedHours: number;
  bookedHours: number;
  approvedForInvoiceHours: number;
  approvedForBillHours: number;
  /**
   * Invoice hours an approve of the still-pending entries would write. NOT `pendingTrackedHours`:
   * an unapprove keeps the entry's approved overrides and a re-approve re-applies them, so an
   * entry unapproved at 5h but tracked at 1h is approved at 5h again.
   */
  pendingApprovedForInvoiceHours: number;
  /** Same as {@link pendingApprovedForInvoiceHours}, for bill generation. */
  pendingApprovedForBillHours: number;
  approvalPercentage: number; // (approvedForInvoiceHours / bookedHours) * 100
  totalWorklogs: number;
  /** Number of worklog entries already approved (unapprovable count). */
  approvedWorklogs: number;
  /** Number of worklog entries still awaiting approval (approvable count). */
  pendingWorklogs: number;
  hasEmptyDescription: boolean;
  timeOffHours: number;
  approvedTimeOffHours: number;
  pendingTimeOffHours: number;
  /** Member's effective external rate on the project for the period, in `currencyCode`. */
  externalRate: number | null;
  /** Approved-for-invoice hours × external rate for the period, in `currencyCode`. */
  approvedIncome: number | null;
  currencyCode: string | null;
  timeEntries: ApprovalEntryRow[];
  hasChildren?: boolean;
  hash: string;
}

/**
 * Project row in approvals table (top level)
 * Contains aggregated hours and list of members
 */
export interface ApprovalProjectRow {
  id: string;
  project: ShortProject;
  trackedHours: number;
  /**
   * Tracked hours of the entries still awaiting approval. This is a TRACKED figure for display —
   * what an approve would actually write is {@link pendingApprovedForInvoiceHours}.
   */
  pendingTrackedHours: number;
  bookedHours: number;
  approvedForInvoiceHours: number;
  approvedForBillHours: number;
  /**
   * Invoice hours an approve of the still-pending entries would write. NOT `pendingTrackedHours`:
   * an unapprove keeps the entry's approved overrides and a re-approve re-applies them, so an
   * entry unapproved at 5h but tracked at 1h is approved at 5h again.
   */
  pendingApprovedForInvoiceHours: number;
  /** Same as {@link pendingApprovedForInvoiceHours}, for bill generation. */
  pendingApprovedForBillHours: number;
  approvalPercentage: number; // (approvedForInvoiceHours / bookedHours) * 100
  totalWorklogs: number;
  /** Number of worklog entries already approved (unapprovable count). */
  approvedWorklogs: number;
  /** Number of worklog entries still awaiting approval (approvable count). */
  pendingWorklogs: number;
  hasEmptyDescription: boolean;
  timeOffHours: number;
  approvedTimeOffHours: number;
  pendingTimeOffHours: number;
  /** Effective external rate on the project for the period (weighted average), in `currencyCode`. */
  externalRate: number | null;
  /** Approved-for-invoice hours × external rate for the period, in `currencyCode`. */
  approvedIncome: number | null;
  currencyCode: string | null;
  members: ApprovalMemberRow[];
  hasChildren?: boolean;
  hash: string;
}

/**
 * A pair of hour figures split by purpose — the same split the Approved column and the summary
 * cards show. Both sides carry the same number whenever nothing distinguishes them (a pending
 * entry with no inline edit is approved at its tracked hours on both sides).
 */
export interface ApprovalHoursSplit {
  /** Hours invoice generation would bill the client for. */
  invoice: number;
  /** Hours bill generation would pay the member for. */
  bill: number;
}

// =============================================================================
// Selection model
// =============================================================================
// The approve/unapprove API supports three granular selection levels (projects,
// team members, worklogs), each requiring only ids + hashes. Only ONE level is
// active at a time — selecting a row on one level clears the others.
//
// A project/member selection may additionally carry EXCLUSIONS: rows the user
// explicitly unchecked underneath it. Exclusions are snapshots taken at uncheck
// time (the row may later collapse out of the loaded tree), keep every other
// checkbox intact, and are sent to the API as excluded worklog/user ids.
//
// It also carries the EDITED ENTRIES it covers — loaded worklogs with pending inline
// edits. The API's aggregate items can't carry per-entry approved minutes/notes, so
// those entries are excluded from the aggregate item and sent as worklog-level items
// in the same request (see `mapApprovalSelectionToRequest`).
//
// Selection-counting logic lives in `helpers/approval-selection-counts.helper.ts`.
// =============================================================================

export enum ApprovalSelectionLevel {
  Projects = 'projects',
  Members = 'members',
  Worklogs = 'worklogs',
}

export enum ApprovalExclusionKind {
  Entry = 'entry',
  Member = 'member',
}

/** A worklog entry explicitly unchecked under a selected project/member row. */
export interface ApprovalEntryExclusion {
  kind: ApprovalExclusionKind.Entry;
  /** Row id of the excluded entry (`entry-{id}`). */
  rowId: string;
  /** Row id of the SELECTED ancestor this exclusion punches a hole in. */
  ancestorRowId: string;
  /** Row id of the member row the entry sits under (for member re-check cleanup). */
  parentRowId: string;
  entryId: number;
  status: TimeEntryStatus;
  /**
   * Approved hours at uncheck time, per side. A PENDING entry carries what an approve would
   * write for it (its overrides, or its tracked hours); an APPROVED one carries what an
   * unapprove would clear. Both counts subtract these.
   */
  approvedForInvoiceHours: number;
  approvedForBillHours: number;
}

/** A member row explicitly unchecked under a selected project row. */
export interface ApprovalMemberExclusion {
  kind: ApprovalExclusionKind.Member;
  rowId: string;
  /** Row id of the SELECTED ancestor (project) this exclusion punches a hole in. */
  ancestorRowId: string;
  userId: number;
  pendingWorklogs: number;
  approvedWorklogs: number;
  /** Approved hours the member's PENDING entries would be approved at — drives the approve count. */
  pendingApprovedForInvoiceHours: number;
  pendingApprovedForBillHours: number;
  /** Approved hours of the member's APPROVED entries — drives the unapprove count. */
  approvedForInvoiceHours: number;
  approvedForBillHours: number;
}

export type ApprovalExclusion = ApprovalEntryExclusion | ApprovalMemberExclusion;

/**
 * A worklog under an aggregate selection that carries a pending inline edit (custom approved
 * hours / notes).
 *
 * An aggregate request item can't express per-entry approved minutes, so these are split out of
 * it and sent as worklog-level items (see `mapApprovalSelectionToRequest`).
 */
export interface ApprovalEditedEntry {
  /** The row with the edit merged in — what the request item and the counts must use. */
  entry: ApprovalTimeEntry;
  /**
   * The entry's approved hours BEFORE the edit — what the aggregate row's
   * `pendingApprovedFor*Hours` already counts for it. The counts swap this out for the edited
   * value, so the delta is per side and an untouched side contributes nothing.
   */
  baseline: ApprovalHoursSplit;
}

export type ApprovalEditedEntries = ApprovalEditedEntry[];

/** A selected project row together with the exclusions recorded underneath it. */
export interface ApprovalSelectedProject {
  project: ApprovalProjectRow;
  exclusions: ApprovalExclusion[];
  editedEntries: ApprovalEditedEntries;
}

/** A selected member row together with its parent project context (needed for the API payload). */
export interface ApprovalSelectedMember {
  member: ApprovalMemberRow;
  projectId: number;
  projectName: string;
  /** Entry exclusions recorded underneath this member selection. */
  exclusions: ApprovalEntryExclusion[];
  editedEntries: ApprovalEditedEntries;
}

/** Discriminated selection payload passed to the confirm modal and approve/unapprove mutations. */
export type ApprovalSelection =
  | { level: ApprovalSelectionLevel.Projects; projects: ApprovalSelectedProject[] }
  | { level: ApprovalSelectionLevel.Members; members: ApprovalSelectedMember[] }
  | { level: ApprovalSelectionLevel.Worklogs; entries: ApprovalTimeEntry[] };

/** Approvable / unapprovable totals of a selection (entry counts + the hours they carry). */
export interface ApprovalSelectionCounts {
  /** Entries that can be approved (pending / awaiting). */
  approveCount: number;
  /** Entries that can be unapproved (already approved). */
  unapproveCount: number;
  /**
   * Hours an approve would write, per side. They differ whenever an entry carries an inline edit
   * on one side only, or an approved override retained by an earlier unapprove — which is why
   * this is a split and not one headline figure.
   */
  approveHours: ApprovalHoursSplit;
  /** Approved hours an unapprove would clear, per side. */
  unapproveHours: ApprovalHoursSplit;
}

/**
 * Approvals summary statistics
 */
export interface ApprovalsSummary {
  awaitingApprovalHours: number;
  /** Approved hours invoice generation would bill the client for, over the filtered period. */
  approvedForInvoiceHours: number;
  /** Approved hours bill generation would pay members for, over the filtered period. */
  approvedForBillHours: number;
  /** One count for both values — an entry is either pending or approved. */
  entriesPending: number;
  /** Approved-for-invoice ÷ booked, as a percentage. */
  invoiceApprovalPercentage: number;
  /** Approved-for-bill ÷ booked, as a percentage. */
  billApprovalPercentage: number;
  totalWorklogs: number;
  trackedHours: number;
  bookedHours: number;
  /** Organization-currency income totals; null when the user lacks the billing permission. */
  approvedIncome: number | null;
  pendingIncome: number | null;
  currencyCode: string | null;
}

/**
 * Full approvals response
 */
export interface ApprovalsResponse {
  summary: ApprovalsSummary;
  projects: ApprovalProjectRow[];
}

// =============================================================================
// Load-more sentinel rows
// =============================================================================
// Synthetic rows injected as the LAST child of a section that is currently fetching
// its next page. They render as a spinner centered across the table's visible width and
// are excluded from selection, expansion, and section-tail detection.
//
// Constructors and guards live in `helpers/approval-load-more-row.helper.ts`.
// =============================================================================

/**
 * Synthetic sentinel row (loading-more OR empty-section placeholder). Carries only an id and
 * a variant marker (for the row model). It is NOT one of the real row shapes — every helper
 * checks `isLoadMoreRow` first so it never reaches the project/member/entry branches. The two
 * variants share that exclusion path (selection, expansion, section-tail detection); only the
 * full-width render differs (`spinner` vs the empty state).
 */
export interface ApprovalLoadMoreRow {
  id: string;
  isLoadMore: true;
  variant: 'spinner' | 'empty';
}

/**
 * Union type for any approval table row (used for getSubRows/getRowId). Includes the
 * synthetic load-more sentinel so it flows through the row model like any other row.
 *
 * Type guards and row-model helpers (getApprovalRowId, getApprovalSubRows,
 * hasApprovalChildren) live in `helpers/approval-row-model.helper.ts`.
 */
export type ApprovalTableRow =
  | ApprovalProjectRow
  | ApprovalMemberRow
  | ApprovalTimeEntry
  | ApprovalTimeOffEntry
  | ApprovalLoadMoreRow;
